import React from "react";

import { faCircleCheck, faInfoCircle, faPlus } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonStandard, Icon, Text } from "@performyard/py-components";

import type { FormQuestionSuggestionStatus, FormQuestionSuggestionWithId } from "../types";

import { QUESTION_TYPE_LABEL } from "../utils/question-type-labels";

type FormQuestionPreviewCardProps = {
	suggestion: FormQuestionSuggestionWithId;
	status: FormQuestionSuggestionStatus;
	/** Rebuilt from saved history — keeps "Question Added" if it was added, otherwise shows no action. */
	isRestored?: boolean;
	onAdd: () => void;
};

const getResponseOptions = (suggestion: FormQuestionSuggestionWithId): string[] => {
	if (suggestion.type === "rating-scale") {
		return suggestion.rating_scale?.map((option) => option.name) ?? [];
	}
	if (suggestion.type === "multiple-choice" || suggestion.type === "multiple-select") {
		return suggestion.choices ?? [];
	}
	return [];
};

const getQualifier = (suggestion: FormQuestionSuggestionWithId): string | undefined => {
	if (suggestion.type !== "numeric" || suggestion.min === undefined || suggestion.max === undefined) {
		return undefined;
	}
	return `${suggestion.min}–${suggestion.max}`;
};

export const FormQuestionPreviewCard: React.FC<FormQuestionPreviewCardProps> = ({
	suggestion,
	status,
	isRestored,
	onAdd,
}) => {
	if (status === "deleted") return null;

	const isAdded = status === "added";
	const responseOptions = getResponseOptions(suggestion);
	const isRatingScale = suggestion.type === "rating-scale";
	const qualifier = getQualifier(suggestion);

	return (
		<div className="box-border flex max-w-full min-w-0 flex-col gap-2 rounded-md border border-solid border-neutral-3 bg-neutral-0 px-3 py-4">
			<div className="flex max-w-full min-w-0 flex-col gap-1">
				<Text size="caption" weight="semi-bold" color="neutral-9" dangerouslySetClassName="min-w-0">
					{QUESTION_TYPE_LABEL[suggestion.type]}
				</Text>

				<div className="flex min-w-0 flex-col gap-0.5">
					<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
						{suggestion.question}
						{suggestion.is_required ? <span className="text-red-7"> *</span> : null}
					</Text>
					{suggestion.subtext ? (
						<Text size="caption" color="neutral-7">
							{suggestion.subtext}
						</Text>
					) : null}
				</div>

				{qualifier ? (
					<Text size="caption" color="neutral-9">
						{qualifier}
					</Text>
				) : null}

				{responseOptions.length > 0 ? (
					<ul className="m-0 flex list-none flex-col gap-1 p-0">
						{responseOptions.map((option, idx) => (
							<li key={`${suggestion.id}-option-${idx}`} className="flex items-center gap-1">
								{isRatingScale ? (
									<Icon icon={faInfoCircle} color="neutral-2" size={12} />
								) : (
									<span
										aria-hidden
										className="inline-block h-3 w-3 shrink-0 rounded-full border border-solid border-neutral-4 bg-transparent"
									/>
								)}
								<Text size="caption" color="neutral-9">
									{option}
								</Text>
							</li>
						))}
					</ul>
				) : null}
			</div>

			{isAdded ? (
				<ButtonStandard
					variant="outline"
					color="green"
					size="small"
					iconLeft={faCircleCheck}
					onClick={() => undefined}
					dangerouslySetClassName="pointer-events-none cursor-default"
				>
					Question Added
				</ButtonStandard>
			) : (
				// A restored card that was never added has nothing left to offer — the form
				// it targeted may not be the one open now — so the action is hidden, not
				// shown dead. Only an added one keeps its state (the confirmation above).
				!isRestored && (
					<ButtonStandard
						variant="outline"
						color="primary"
						size="small"
						iconLeft={faPlus}
						onClick={onAdd}
						data-testid="askpy-add-question-button"
					>
						Add Question
					</ButtonStandard>
				)
			)}
		</div>
	);
};
